// Client for the token-level endpoints of the public Panther API: one token's
// detail, its history, and every tokenized version of the same stock across
// venues. Response shapes below are the live ones (checked with curl against
// api.pantherterminal.com on 2026-10-06). Unknown values are null, never 0.

import { API_BASE, type BoardDeployment } from "./api";
import type { FlowFields } from "./flows";

// ---------------------------------------------------------------- GET /v1/tokens/{id}

export interface TokenDetail {
  schema: "panther.token.v1";
  /** Slot of the latest observation; null before the first collection. */
  as_of: string | null;
  token: {
    /** `<chain>:<address>`, lower-cased by the collector. */
    id: string;
    venue: { id: string; name: string };
    chain: string;
    /** Every chain the token is live on (deployments with supply, plus the primary one). */
    chains: string[];
    address: string;
    deployments: BoardDeployment[] | null;
    symbol: string;
    name: string | null;
    decimals: number | null;
    /** `sym:<TICKER>` when the token maps to a listed stock. */
    asset_id: string | null;
    ticker: string | null;
    isin: string | null;
    logo_url: string | null;
    status: string;
    /** "issuer" (venue quote), "market" (exchange trade) or null when unknown. */
    price_basis: string | null;
    price_provider: string | null;
    first_seen: string | null;
    last_seen: string | null;
  };
  latest: TokenLatest | null;
  links: { history: string; asset: string | null };
}

export interface TokenLatest extends FlowFields {
  slot: string;
  price: number | null;
  bid: number | null;
  ask: number | null;
  underlying_price: number | null;
  multiplier: number | null;
  price_at: string | null;
  supply: number | null;
  market_cap: number | null;
  holders: number | null;
  holders_at: string | null;
  volume_usd: number | null;
  mint_burn_usd: number | null;
  transfers_24h: number | null;
  halted: boolean | null;
  change_24h: { price: number | null; market_cap: number | null; supply: number | null };
}

// ---------------------------------------------------------------- GET /v1/tokens/{id}/history

export const HISTORY_RANGES = ["24h", "7d", "30d", "all"] as const;
export type HistoryRange = (typeof HISTORY_RANGES)[number];

export interface HistoryPoint {
  /** UTC date of the point, YYYY-MM-DD. */
  date?: string;
  slot: string;
  price: number | null;
  market_cap: number | null;
  supply: number | null;
  holders: number | null;
  volume_usd: number | null;
  mint_burn_usd: number | null;
  transfers_24h: number | null;
  /** That day's net flow, USD (C-6c); null when not measured. */
  net_flow_usd?: number | null;
}

export interface TokenHistory {
  schema: "panther.history.v1";
  as_of: string | null;
  token: { id: string; venue: string; chain: string; symbol: string; name: string | null; asset_id: string | null };
  range: HistoryRange;
  /** Bucket width: "30m" for 24h, "1h" for 7d, "6h" for 30d, "1d" for all. */
  interval: string;
  sampling: string;
  from: string | null;
  to: string | null;
  /** Raw 30-minute observations in the window, before bucketing. */
  observations: number;
  /** Buckets kept: one point each, the last observation in the bucket. */
  points: number;
  series: HistoryPoint[];
}

// ---------------------------------------------------------------- GET /v1/assets/{ticker}

export interface AssetToken {
  id: string;
  venue: { id: string; name: string };
  chain: string;
  chains: string[];
  address: string;
  symbol: string;
  name: string | null;
  as_of: string | null;
  /** Latest observation more than 24h older than the newest slot anywhere; left out of totals. */
  stale: boolean;
  price: number | null;
  multiplier: number | null;
  market_cap: number | null;
  supply: number | null;
  holders: number | null;
  volume_usd: number | null;
  link: string;
}

export interface AssetDetail {
  schema: "panther.asset.v1";
  as_of: string | null;
  asset: { id: string; ticker: string; symbol: string; name: string | null; isin: string | null; asset_type: string | null };
  totals: {
    venues: number;
    tokens: number;
    priced: number;
    market_cap: number | null;
    volume_usd: number | null;
    stale_tokens: number;
  };
  notes: string;
  tokens: AssetToken[];
}

// ---------------------------------------------------------------- fetching

/** The API rejected the request: unknown token or ticker (404) or a malformed id (400). */
export class ApiRejected extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function getJson<T extends { schema: string }>(path: string, schema: T["schema"], signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, { signal });
  if (response.status === 404 || response.status === 400) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiRejected(response.status, body?.error ?? (response.status === 404 ? "Not found" : "Bad request"));
  }
  if (!response.ok) throw new Error(`The API answered ${response.status}`);
  const body = (await response.json()) as { schema?: string };
  if (body.schema !== schema) throw new Error("Unexpected response format");
  return body as T;
}

export const tokenPath = (id: string) => `/v1/tokens/${encodeURIComponent(id)}`;
export const assetPath = (ticker: string) => `/v1/assets/${encodeURIComponent(ticker)}`;

export const fetchToken = (id: string, signal?: AbortSignal) => getJson<TokenDetail>(tokenPath(id), "panther.token.v1", signal);

export const fetchHistory = (id: string, range: HistoryRange, signal?: AbortSignal) =>
  getJson<TokenHistory>(`${tokenPath(id)}/history?range=${range}`, "panther.history.v1", signal);

/** The daily series (?days=): one point per UTC day, which is what the net flow bars read. */
export const fetchDailyHistory = (id: string, days: number, signal?: AbortSignal) =>
  getJson<TokenHistory>(`${tokenPath(id)}/history?days=${days}`, "panther.history.v1", signal);

export const fetchAsset = (ticker: string, signal?: AbortSignal) => getJson<AssetDetail>(assetPath(ticker), "panther.asset.v1", signal);

// ---------------------------------------------------------------- id and ticker helpers

/**
 * Token ids are `<chain>:<address>`; Stellar ids carry a code segment
 * (`stellar:<code>:<issuer>`). Same rule as the API's parseTokenId, including
 * the lower-casing the collector applies (see roadmap P-10 for Solana).
 */
const TOKEN_ID = /^[a-z0-9-]{2,32}:[a-z0-9._:-]{1,120}$/;

export interface TokenId {
  id: string;
  chain: string;
  address: string;
}

/** Normalises a raw id (from a URL or a row); null when it is not a token id. */
export function parseTokenId(raw: string | null | undefined): TokenId | null {
  if (!raw) return null;
  const id = raw.trim().toLowerCase();
  if (!TOKEN_ID.test(id)) return null;
  const colon = id.indexOf(":");
  return { id, chain: id.slice(0, colon), address: id.slice(colon + 1) };
}

/** The listed ticker behind an asset id (`sym:NVDA` → `NVDA`); null for other id kinds. */
export const tickerOf = (assetId: string | null | undefined): string | null =>
  assetId && assetId.startsWith("sym:") && assetId.length > 4 ? assetId.slice(4) : null;

/** `0xd0601ce157db5bdc3162bbac2a2c8af5320d9eec` → `0xd060…d9eec`; short ids stay whole. */
export function shortAddress(address: string, head = 6, tail = 5): string {
  return address.length <= head + tail + 1 ? address : `${address.slice(0, head)}…${address.slice(-tail)}`;
}

/** Sum of the known values, with how many of the inputs were known. Null when none is. */
export function knownTotal(values: ReadonlyArray<number | null | undefined>): { total: number | null; known: number; of: number } {
  let total: number | null = null;
  let known = 0;
  for (const value of values) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    total = (total ?? 0) + value;
    known += 1;
  }
  return { total, known, of: values.length };
}

/** Lowest and highest of the known values, or null when none is known. */
export function knownRange(values: ReadonlyArray<number | null | undefined>): { min: number; max: number } | null {
  let range: { min: number; max: number } | null = null;
  for (const value of values) {
    if (typeof value !== "number" || !Number.isFinite(value)) continue;
    range = range ? { min: Math.min(range.min, value), max: Math.max(range.max, value) } : { min: value, max: value };
  }
  return range;
}
